import { MAX_DETECTED_FACES } from '@familienbaum/shared';
import type { FaceDetector } from '@mediapipe/tasks-vision';
import type { FaceLandmarks, Point } from './embed';

/**
 * Gesichter in einem Bild finden – Rechtecke, keine Namen.
 *
 * Dahinter steckt BlazeFace aus Google MediaPipe: ein kleines neuronales Netz
 * (230 KB), das im Browser läuft. Kein Bild verlässt dabei das Gerät, und der
 * Server bekommt am Ende nur vier Zahlen pro Gesicht.
 *
 * Bewusst *nur* Erkennung, keine Wiedererkennung. BlazeFace liefert kein
 * Merkmal, mit dem sich zwei Gesichter vergleichen ließen – wer auf dem Bild zu
 * sehen ist, entscheidet weiterhin ein Mensch. In einer Familienchronik ist das
 * auch die richtige Reihenfolge: das Rechteck ist Fleißarbeit, der Name ist
 * Wissen.
 *
 * ## Warum in mehreren Durchgängen
 *
 * BlazeFace („short range") rechnet intern auf 128 × 128 Bildpunkten. Ein
 * Gesicht muss darin etwa ein Achtel der Kantenlänge einnehmen, um gefunden zu
 * werden. Bei einem Porträt ist das mühelos erfüllt; bei einer
 * Hochzeitsgesellschaft mit dreißig Leuten ist jedes einzelne Gesicht viel zu
 * klein – ein Durchgang über das ganze Bild findet dort fast nichts.
 *
 * Deshalb wird das Bild zusätzlich in Kacheln zerlegt und jede Kachel für sich
 * untersucht. In einer Kachel von einem Drittel der Bildbreite ist dasselbe
 * Gesicht dreimal so groß – und wird gefunden. Drei Stufen (ganzes Bild, 2 × 2,
 * 3 × 3) ergeben 14 Durchgänge und reichen bis etwa zwanzig Personen im Bild.
 * Fehlen bei sehr großen Gruppen noch Gesichter, ist {@link LEVELS} die
 * Stellschraube – jede weitere Stufe kostet n² Durchgänge.
 *
 * Die Kacheln überlappen sich um {@link OVERLAP}, sonst würde ein Gesicht genau
 * auf einer Trennlinie in zwei Hälften geschnitten und in beiden Kacheln
 * verfehlt. Dieselben Gesichter tauchen dadurch mehrfach auf; {@link mergeFaces}
 * räumt das hinterher auf.
 */

export interface DetectedFace {
  /** Normalisiert auf 0…1, wie überall bei den Markierungen. */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Sicherheit des Modells, 0…1. */
  confidence: number;
}

/** Rechteck ohne Bewertung – passt auf `MediaRegion` genauso wie auf `Box`. */
interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Die WASM-Laufzeit liegt unter `/mediapipe/`, das Modell unter `/models/`.
 * Beides vom eigenen Server, kein CDN – siehe `README.md` daneben.
 */
const WASM_BASE = '/mediapipe';
const MODEL_URL = '/models/blaze_face_short_range.tflite';

/**
 * Ab welcher Sicherheit ein Fund gilt.
 *
 * Gemessen an neun Bildern – Schwarzweiß, Sepia und Farbe, Porträts und
 * Gruppen. Über alle hinweg trennt 0,60 sauber: echte Gesichter lagen bei 0,63
 * bis 0,98, Fehlfunde bei 0,30 bis 0,57. Nur zwei echte Gesichter lagen
 * darunter (beide bei 0,52), und zwei Landschaftsbilder ohne einen einzigen
 * Menschen kamen nicht über 0,44 – bei 0,60 bleiben sie leer.
 *
 * Die Zahl gilt bewusst für Farbe *und* Schwarzweiß. Eine frühere Fassung
 * unterschied beides und ließ auf altem Material 0,35 zu; das beruhte auf einer
 * Verwechslung zweier Ausschnitte beim Auswerten – der Rahmen mit 0,36 lag auf
 * der Haube eines Babys, nicht auf seinem Gesicht. Richtig gezählt liegen alte
 * Fotos im selben Bereich wie neue. Das Modell ist auf verblasstem Material
 * nicht unsicherer, es findet dort nur weniger.
 *
 * Was 0,60 nicht mehr findet, sind Gesichter in einer Menschenmenge – auf einem
 * Hinterhofbild mit acht Personen in der Ferne kam keines über 0,59. Die sind
 * hier verschmerzbar: dieselben Bilder lieferten vorher drei richtige Rahmen
 * und neun falsche, und wer neun Rahmen löschen muss, um drei zu behalten,
 * setzt die drei lieber selbst.
 */
const MIN_CONFIDENCE = 0.6;

/**
 * Kachelraster: 1 = ganzes Bild, 2 = 2 × 2, und so weiter bis 5 × 5. Zusammen
 * 55 Durchgänge.
 *
 * Die hohen Stufen tragen den Löwenanteil: auf einem Hochzeitsbild fand Stufe 3
 * fünf Gesichter, Stufe 4 sieben weitere. Stufe 1 findet auf Gruppenbildern
 * gar nichts und bleibt trotzdem drin – für das einzelne Porträt, auf dem ein
 * Gesicht das halbe Bild füllt, ist sie die einzige Stufe, die passt.
 *
 * 55 Durchgänge kosten etwa eine halbe Sekunde. Weiter hinauf lohnt es nicht:
 * Stufe 5 brachte auf zwei der drei Prüfbilder nur noch einen Fund dazu.
 */
const LEVELS = [1, 2, 3, 4, 5] as const;

/** Überlappung benachbarter Kacheln, als Anteil der Kachelkante. */
const OVERLAP = 0.25;

/**
 * Kantenlänge, auf die eine Kachel zum Rechnen gebracht wird. Mehr bringt
 * nichts – das Modell verkleinert ohnehin auf 128 – kostet aber Zeit beim
 * Zeichnen.
 */
const TILE_PX = 320;

/**
 * Wie stark der Fund aufgeweitet wird und wie viel davon nach oben geht.
 *
 * BlazeFace umreißt Augenbrauen bis Kinn; Stirn, Haar und Kinnpartie liegen
 * außerhalb. Von Hand gezogene Markierungen fassen mehr, und die Zuschnitte
 * (`FaceCrop`) rechnen mit einem Rahmen um das *ganze* Gesicht. Ohne diese
 * Korrektur wären automatische und eigene Rahmen zwei verschiedene Dinge.
 */
const GROW_X = 0.25;
const GROW_Y = 0.45;
const GROW_UP_SHARE = 0.62;

/** Ab dieser Überdeckung gelten zwei Funde als dasselbe Gesicht. */
const MERGE_IOU = 0.35;

/**
 * Verwirft, was kein Gesicht sein kann: ein Rechteck über fast die ganze
 * Bildkante oder ein Fleck von wenigen Bildpunkten.
 *
 * Gemessen wird am **Fund selbst**, nicht am aufgeweiteten Rahmen, und in
 * Bildpunkten an der **kürzeren Bildkante** – beides war vorher anders und hat
 * genau die Bilder verworfen, für die die Erkennung am leichtesten ist.
 *
 * Der Reihe nach: {@link grow} weitet jeden Fund um ein Viertel in der Breite
 * und um 45 % in der Höhe auf, damit Stirn und Haar mit hineinfallen. Prüft man
 * *danach* gegen zwei Drittel, liegt die wirkliche Grenze für den Fund bei
 * 0,7 / 1,45 ≈ 0,48 der Bildhöhe. Ein Porträt, auf dem das Gesicht die halbe
 * Höhe füllt – die Bildsorte, die BlazeFace mit 0,9 und mehr trifft –, fiel
 * damit lautlos heraus: kein Rahmen, keine Meldung. Die alte Begründung
 * („echte Gesichter nehmen 4 bis 15 % der Bildbreite ein") stammt von
 * Gruppenbildern; auf dem einzelnen Porträt gilt sie nicht.
 *
 * Und weil `width`/`height` Anteile *ihrer jeweiligen* Bildkante sind, hing die
 * Grenze zusätzlich am Seitenverhältnis: dasselbe quadratische Gesicht ergibt im
 * Hochformat 3:4 einen Höhenanteil von 0,25, im Querformat von 0,44. Die
 * kürzere Kante als Bezug macht daraus eine Zahl, die für beide Formate
 * dasselbe bedeutet.
 *
 * 0,9 lässt das ganz nahe Porträt durch und fängt weiter ab, wofür die Grenze
 * gedacht war: den Rahmen um das halbe Bild. Fehlfunde auf Bildern ohne
 * Menschen hält ohnehin {@link MIN_CONFIDENCE} draußen – gemessen kamen sie
 * nicht über 0,44.
 */
const MAX_SIDE = 0.9;
const MIN_SIDE = 0.01;

/**
 * Ab dieser Überdeckung mit einer *vorhandenen* Markierung wird ein Fund
 * verworfen. Großzügiger als {@link MERGE_IOU}: ein von Hand gesetzter Rahmen
 * ist die Wahrheit, ein zweiter daneben wäre nur Arbeit.
 */
const EXISTING_IOU = 0.25;

// ── Modell laden ───────────────────────────────────────────────────────────

let detectorPromise: Promise<FaceDetector> | null = null;

/**
 * Lädt Laufzeit und Modell – einmal pro Sitzung, danach aus dem Speicher.
 *
 * Der dynamische Import hält die 150 KB MediaPipe-JavaScript aus dem
 * Startpaket heraus: wer nie auf den Knopf drückt, lädt sie nie.
 *
 * Erst mit Grafikkarte, dann ohne. Die GPU ist deutlich schneller, fällt aber
 * auf älteren Geräten und in manchen Fernwartungssitzungen aus – dort läuft es
 * eben auf dem Hauptprozessor weiter, statt mit einem WebGL-Fehler abzubrechen.
 */
async function loadDetector(): Promise<FaceDetector> {
  const { FaceDetector: Detector, FilesetResolver } = await import('@mediapipe/tasks-vision');
  const fileset = await FilesetResolver.forVisionTasks(WASM_BASE);

  const create = (delegate: 'GPU' | 'CPU') =>
    Detector.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: MODEL_URL, delegate },
      runningMode: 'IMAGE',
      minDetectionConfidence: MIN_CONFIDENCE,
    });

  try {
    return await create('GPU');
  } catch {
    return await create('CPU');
  }
}

/**
 * `true`, sobald Laufzeit und Modell im Speicher liegen – der zweite Klick
 * kommt dann ohne Wartezeit aus. Nur für die Beschriftung des Knopfes.
 */
export function detectorReady(): boolean {
  return detectorPromise !== null;
}

/**
 * Wie weit über die Markierung hinaus gesucht wird, wenn die Gesichtspunkte
 * eines vorhandenen Rahmens gebraucht werden.
 *
 * Etwas Luft ist nötig: das Modell erkennt ein Gesicht schlechter, wenn es den
 * Bildrand berührt, und von Hand gezogene Rahmen sitzen mal enger, mal weiter.
 */
const LANDMARK_PAD = 0.5;

/** Kantenlänge, auf die für die Punktsuche vergrößert wird. */
const LANDMARK_PX = 256;

/**
 * Augen und Mundmitte zu einer *vorhandenen* Markierung finden – die Punkte,
 * mit denen `embedFace` das Gesicht in die Normlage dreht.
 *
 * Nötig, weil die Markierungen im Archiv ihre Punkte nicht mitbringen: die von
 * Hand gezogenen hatten nie welche, und die automatisch gesetzten haben sie
 * beim Speichern verloren (gespeichert wird ein Rechteck). Also wird auf dem
 * Ausschnitt noch einmal erkannt – ein Durchgang, kein Vergleich zu den 55 der
 * vollen Suche.
 *
 * Gibt `null` zurück, wenn im Rahmen kein Gesicht zu finden ist. Das ist keine
 * Ausnahme, sondern der Normalfall bei einem Rahmen, der danebensitzt – und
 * genau richtig: ein Merkmalsvektor von einem Kragen hätte in der Datenbank
 * nichts zu suchen.
 */
export async function landmarksForRegion(
  image: HTMLImageElement,
  box: Rect,
): Promise<FaceLandmarks | null> {
  detectorPromise ??= loadDetector();
  const detector = await detectorPromise;

  // Ausschnitt mit Luft, innerhalb des Bildes gehalten.
  const padX = box.width * LANDMARK_PAD;
  const padY = box.height * LANDMARK_PAD;
  const x = clamp01(box.x - padX / 2);
  const y = clamp01(box.y - padY / 2);
  const width = Math.min(1 - x, box.width + padX);
  const height = Math.min(1 - y, box.height + padY);

  const sourceX = x * image.naturalWidth;
  const sourceY = y * image.naturalHeight;
  const sourceWidth = width * image.naturalWidth;
  const sourceHeight = height * image.naturalHeight;

  const canvas = document.createElement('canvas');
  const scale = LANDMARK_PX / Math.max(sourceWidth, sourceHeight);
  canvas.width = Math.max(1, Math.round(sourceWidth * scale));
  canvas.height = Math.max(1, Math.round(sourceHeight * scale));
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.imageSmoothingQuality = 'high';
  context.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  // Der Fund, dessen Mitte der Markierungsmitte am nächsten liegt. Im
  // Ausschnitt können Nachbargesichter mit im Bild sein – bei einer Gruppe
  // sitzen sie dicht an dicht.
  let best: { distance: number; points: FaceLandmarks } | null = null;
  for (const detection of detector.detect(canvas).detections) {
    const found = detection.boundingBox;
    if (!found || detection.keypoints.length < 4) continue;

    const centerX = (found.originX + found.width / 2) / canvas.width;
    const centerY = (found.originY + found.height / 2) / canvas.height;
    const distance = Math.hypot(centerX - 0.5, centerY - 0.5);
    if (best && distance >= best.distance) continue;

    // BlazeFace liefert sechs Punkte, normalisiert auf den Ausschnitt. Die
    // ersten vier sind die beiden Augen, die Nasenspitze und die Mundmitte;
    // die Ohrpunkte danach werden nicht gebraucht. Zurückgerechnet wird auf
    // Bildpunkte des Originals, denn genau damit zeichnet `embedFace`.
    const toImage = (index: number): Point => ({
      x: sourceX + detection.keypoints[index]!.x * sourceWidth,
      y: sourceY + detection.keypoints[index]!.y * sourceHeight,
    });

    best = {
      distance,
      // Welcher der beiden ersten Punkte im Bild links liegt, entscheidet die
      // x-Koordinate – nicht die Reihenfolge. So bleibt es richtig, auch wenn
      // MediaPipe seine Zählung einmal umstellt oder das Gesicht abgewandt ist.
      points: (() => {
        const eyeA = toImage(0);
        const eyeB = toImage(1);
        return {
          leftEye: eyeA.x <= eyeB.x ? eyeA : eyeB,
          rightEye: eyeA.x <= eyeB.x ? eyeB : eyeA,
          mouth: toImage(3),
        };
      })(),
    };
  }

  return best?.points ?? null;
}

/**
 * Einen Herzschlag an den Browser abgeben.
 *
 * `detect` rechnet synchron. 55 Durchgänge in einem Zug wären eine halbe
 * Sekunde Stillstand: kein Neuzeichnen, keine Fortschrittsanzeige, ein
 * eingefrorener Knopf.
 *
 * Und ausdrücklich *nicht* `setTimeout`: liegt der Tab im Hintergrund, drosselt
 * der Browser Zeitgeber auf etwa eine halbe Sekunde. Aus 55 Pausen von
 * 0 Millisekunden würden dann 30 Sekunden – wer während des Suchens den Tab
 * wechselt, käme zu einem scheinbar hängenden Knopf zurück. Eine Nachricht an
 * sich selbst über einen `MessageChannel` wird nicht gedrosselt, `requestAnimationFrame`
 * dagegen fällt im Hintergrund ganz aus.
 */
function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = () => {
      channel.port1.close();
      resolve();
    };
    channel.port2.postMessage(null);
  });
}

// ── Geometrie ──────────────────────────────────────────────────────────────

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function intersectionOverUnion(a: Rect, b: Rect): number {
  const left = Math.max(a.x, b.x);
  const top = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.width, b.x + b.width);
  const bottom = Math.min(a.y + a.height, b.y + b.height);
  if (right <= left || bottom <= top) return 0;

  const overlap = (right - left) * (bottom - top);
  const union = a.width * a.height + b.width * b.height - overlap;
  return union > 0 ? overlap / union : 0;
}

/**
 * Der Fund, aufgeweitet auf einen Rahmen, wie ihn ein Mensch ziehen würde:
 * etwas Luft zu den Seiten, deutlich mehr nach oben für Stirn und Haar.
 * Am Bildrand wird gekappt – ein angeschnittenes Gesicht bleibt angeschnitten.
 */
function grow(box: Rect): Rect {
  const dx = box.width * GROW_X;
  const dy = box.height * GROW_Y;
  const x = clamp01(box.x - dx / 2);
  const y = clamp01(box.y - dy * GROW_UP_SHARE);
  return {
    x,
    y,
    width: Math.min(1 - x, box.width + dx),
    height: Math.min(1 - y, box.height + dy),
  };
}

/**
 * Mehrfachfunde zusammenfassen: der sicherste Fund gewinnt, alles was sich
 * stark mit ihm überdeckt, fällt weg (nicht-maximale Unterdrückung).
 *
 * Weil vom sichersten Fund her gearbeitet wird, ist die Obergrenze hier auch
 * die richtige Stelle: gekappt werden die *unsichersten* Funde, nicht die
 * zuletzt gefundenen.
 *
 * Reihenfolge der Rückgabe ist von links nach rechts – so wie die API die
 * Markierungen später ausliefert und wie man ein Bild liest.
 */
function mergeFaces(faces: DetectedFace[]): DetectedFace[] {
  const kept: DetectedFace[] = [];

  for (const face of [...faces].sort((a, b) => b.confidence - a.confidence)) {
    if (kept.length >= MAX_DETECTED_FACES) break;
    if (kept.some((other) => intersectionOverUnion(face, other) > MERGE_IOU)) continue;
    kept.push(face);
  }

  return withoutClusters(kept).sort((a, b) => a.x - b.x);
}

/**
 * Rechtecke wegwerfen, in denen mehrere andere Funde liegen.
 *
 * Auf einem Bild mit vielen kleinen Gesichtern setzt das Modell gelegentlich
 * einen großen Rahmen um eine ganze Menschentraube – auf einem
 * Hochzeitsbild einen, der 45 % der Bildbreite einnahm, während jedes echte
 * Gesicht bei 4 bis 11 % lag. Von außen betrachtet ist das keine unsichere
 * Erkennung, sondern eine Verwechslung der Größenordnung: viele Gesichter
 * zusammen sehen von weit weg wieder wie ein Gesicht aus.
 *
 * Bleibt bestehen, obwohl {@link MIN_CONFIDENCE} den einen beobachteten Fall
 * schon abfängt (er lag bei 0,40): die Schwelle bewertet einen Rahmen für sich,
 * dieser Filter sein *Verhältnis zu den anderen*. Ein Rahmen um eine ganze
 * Gruppe ist auch bei hoher Sicherheit falsch, und {@link MAX_SIDE} allein
 * lässt ihn durch.
 *
 * Geprüft wird über die Mittelpunkte und nicht über die Überdeckung: in einer
 * dichten Gruppe berühren sich benachbarte Rahmen ständig, aber der Mittelpunkt
 * eines Gesichts liegt nur dann in einem anderen Rahmen, wenn dieser es
 * wirklich umfasst. Zwei enthaltene Funde sind die Grenze – bei einem könnte es
 * ein Kind auf dem Arm sein, das sich mit dem Erwachsenen überschneidet.
 */
function withoutClusters(faces: DetectedFace[]): DetectedFace[] {
  if (faces.length < 3) return faces;

  const contains = (outer: Rect, inner: Rect) => {
    const cx = inner.x + inner.width / 2;
    const cy = inner.y + inner.height / 2;
    return (
      cx > outer.x &&
      cx < outer.x + outer.width &&
      cy > outer.y &&
      cy < outer.y + outer.height
    );
  };

  return faces.filter(
    (face) =>
      faces.filter((other) => other !== face && contains(face, other)).length < 2,
  );
}

/**
 * Funde weglassen, die auf einer schon vorhandenen Markierung liegen. Ein
 * zweiter Durchlauf auf demselben Bild soll nichts verdoppeln.
 */
export function withoutExisting(faces: DetectedFace[], existing: Rect[]): DetectedFace[] {
  if (existing.length === 0) return faces;
  return faces.filter(
    (face) => !existing.some((region) => intersectionOverUnion(face, region) > EXISTING_IOU),
  );
}


// ── Kacheln ────────────────────────────────────────────────────────────────

/**
 * Die Ausschnitte für alle Stufen, in normalisierten Koordinaten.
 *
 * Bei `n` Kacheln je Achse ist eine Kachel `1/n` breit und wird um `OVERLAP`
 * ihrer Kante aufgeweitet; die Mittelpunkte bleiben gleichmäßig verteilt. Die
 * Randkacheln ragen dadurch über das Bild hinaus und werden zurückgeschoben.
 */
function tiles(): Rect[] {
  const out: Rect[] = [];

  for (const n of LEVELS) {
    const size = Math.min(1, (1 / n) * (1 + OVERLAP));
    for (let row = 0; row < n; row++) {
      for (let column = 0; column < n; column++) {
        out.push({
          x: Math.min(1 - size, Math.max(0, (column + 0.5) / n - size / 2)),
          y: Math.min(1 - size, Math.max(0, (row + 0.5) / n - size / 2)),
          width: size,
          height: size,
        });
      }
    }
  }

  return out;
}

/** Einmal gerechnet – die Ausschnitte hängen an nichts, was sich ändert. */
const PASSES = tiles();

export const DETECTION_PASSES = PASSES.length;

// ── Hauptweg ───────────────────────────────────────────────────────────────

export interface DetectOptions {
  /** Nach jedem Durchgang aufgerufen – für die Fortschrittsanzeige. */
  onProgress?: (done: number, total: number) => void;
}

/**
 * Gesichter in einem geladenen Bild finden.
 *
 * Erwartet ein `<img>`, das fertig geladen ist und **gleiche Herkunft** hat
 * (bei Familienbaum immer der Fall: die Ableitungen kommen unter
 * `/api/media/…` vom eigenen Server). Ein fremdes Bild würde die Zeichenfläche
 * sperren und beim Auslesen einen Sicherheitsfehler auslösen.
 */
export async function detectFaces(
  image: HTMLImageElement,
  { onProgress }: DetectOptions = {},
): Promise<DetectedFace[]> {
  const sourceWidth = image.naturalWidth;
  const sourceHeight = image.naturalHeight;
  if (!sourceWidth || !sourceHeight) return [];

  detectorPromise ??= loadDetector();
  let detector: FaceDetector;
  try {
    detector = await detectorPromise;
  } catch (error) {
    // Ein gescheiterter Ladeversuch darf sich nicht festsetzen – sonst
    // schlägt jeder weitere Klick sofort fehl, auch wenn es nur die Verbindung
    // war, die kurz weg war.
    detectorPromise = null;
    throw error;
  }

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: false });
  if (!context) throw new Error('Zeichenfläche nicht verfügbar');
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';

  /** Bezugsgröße des Größenfilters – siehe {@link MAX_SIDE}. */
  const shortEdge = Math.min(sourceWidth, sourceHeight);

  const found: DetectedFace[] = [];

  for (const [index, tile] of PASSES.entries()) {
    // Die Zeichenfläche behält das Seitenverhältnis der Kachel: gestaucht
    // würde das Modell schlechter finden, und die Rückrechnung wäre schief.
    const pixelWidth = tile.width * sourceWidth;
    const pixelHeight = tile.height * sourceHeight;
    const scale = TILE_PX / Math.max(pixelWidth, pixelHeight);
    canvas.width = Math.max(1, Math.round(pixelWidth * scale));
    canvas.height = Math.max(1, Math.round(pixelHeight * scale));

    context.drawImage(
      image,
      tile.x * sourceWidth,
      tile.y * sourceHeight,
      pixelWidth,
      pixelHeight,
      0,
      0,
      canvas.width,
      canvas.height,
    );

    for (const detection of detector.detect(canvas).detections) {
      const box = detection.boundingBox;
      const confidence = detection.categories[0]?.score ?? 0;
      if (!box || confidence < MIN_CONFIDENCE) continue;

      // Bildpunkte der Kachel → Anteil der Kachel → Anteil des Bildes.
      const find = {
        x: tile.x + (box.originX / canvas.width) * tile.width,
        y: tile.y + (box.originY / canvas.height) * tile.height,
        width: (box.width / canvas.width) * tile.width,
        height: (box.height / canvas.height) * tile.height,
      };

      // Größe am Fund messen, in Bildpunkten und an der kürzeren Bildkante –
      // siehe {@link MAX_SIDE}. Erst danach aufweiten.
      const longSide = Math.max(find.width * sourceWidth, find.height * sourceHeight) / shortEdge;
      const shortSide = Math.min(find.width * sourceWidth, find.height * sourceHeight) / shortEdge;
      if (longSide > MAX_SIDE || shortSide < MIN_SIDE) continue;

      found.push({ ...grow(find), confidence });
    }

    onProgress?.(index + 1, PASSES.length);
    await yieldToBrowser();
  }

  return mergeFaces(found);
}
